import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SurveyForm } from '../components/survey/SurveyForm';
import { ArrowLeft, Share2, FileText, Circle, PauseCircle, Info } from 'lucide-react';
import { ShareSurveyModal } from './survey/share/ShareSurveyModal';
import useSurveys from '@hooks/useSurveys';
import { useSurvey } from '@contexts/SurveyContext';
import { useRecording } from '@contexts/RecordingContext';
import { useTeam } from '@contexts/TeamContext';
import { Toast } from '@common/Toast';
import { RecordingStatusToast } from './RecordingStatusToast';
import { RecordingStatsModal } from './status/RecordingStatsModal';
import { LocationInfoModal } from './status/LocationInfoModal';
import { DatabaseStatusModal } from './status/DatabaseStatusModal';
import { Header } from './RoadScopePage/Header';
import { QuickActions } from './RoadScopePage/QuickActions';
import { Modals } from './RoadScopePage/Modals';
import { useToast } from './RoadScopePage/hooks/useToast';
import { useModals } from './RoadScopePage/hooks/useModals';
import { HighPolePOISelector } from '../components/highpole';
import { useSurveyActions } from './RoadScopePage/hooks/useSurveyActions';
import { SurveyList } from './RoadScopePage/SurveyList';

function RoadScopePage() {
  const [showEditSurvey, setShowEditSurvey] = useState(false);
  const [surveyToEdit, setSurveyToEdit] = useState(null);
  const navigate = useNavigate();
  const [showReports, setShowReports] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const { surveys, loading, error, refetch } = useSurveys(false);
  const { activeSurvey, startSurvey, endSurvey } = useSurvey();
  const { isRecording, toggleRecording } = useRecording();
  const [showRecordingStats, setShowRecordingStats] = useState(false);
  const [showLocationInfo, setShowLocationInfo] = useState(false);
  const [showDatabaseStatus, setShowDatabaseStatus] = useState(false);
  const { teamMembers } = useTeam();
  const { toast, setToast } = useToast();
  const { modalStates, modalHandlers } = useModals();
  const { handleSurveyEdit, handleLoadSurvey, handleViewMap } = useSurveyActions({ setToast });
  const [showNewSurveyForm, setShowNewSurveyForm] = useState(false);

  const handlePOIEditor = () => {
    if (!activeSurvey) {
      setToast({
        type: 'error',
        message: 'Please select a survey first'
      });
      return;
    }
    
    navigate('pois', {
      state: { surveyId: activeSurvey.id }
    });
  };

  const handleSharedMap = () => {
    if (!activeSurvey) return;
    setShowShare(true);
  };

  const handleNewSurvey = () => {
    setShowNewSurveyForm(true);
  };

  const handleSurveyUpdate = async (data) => {
    await handleSurveyEdit(data);
    refetch();
  };

  return (
    <div className="min-h-screen bg-[#3D3F42]">
      <div className="sticky top-0 bg-[#2A2B2E] shadow-sm z-10 p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">RoadScope</h1>
            <p className="text-sm text-gray-400">Survey, Advanced Mapping & Routes Analyzer</p>
          </div>
        </div>
        
        {activeSurvey && (
          <div className="mt-6 bg-blue-500/10 p-4 rounded-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* Recording Status Toast - Positioned within active survey container */}
              {isRecording && <RecordingStatusToast />}
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-medium text-white">{activeSurvey.name}</h2>
                  {activeSurvey.status === 'paused' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded-full text-xs">
                      <PauseCircle className="w-3 h-3" />
                      Tracking Paused
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400 mt-1">Active Survey</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowShare(true)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
                <button
                  onClick={() => modalHandlers.setShowReports(true)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Reports
                </button>
                <button
                  onClick={toggleRecording}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 
                    ${isRecording 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : activeSurvey?.status === 'paused'
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  disabled={!activeSurvey}
                >
                  <Circle className={`w-4 h-4 ${isRecording ? 'animate-pulse' : ''}`} />
                  {isRecording ? 'Stop Recording' : 
                   activeSurvey?.status === 'paused' ? 'Resume Recording' : 
                   'Start Recording'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <QuickActions 
        activeSurvey={activeSurvey}
        isRecording={isRecording}
        onRecordingStats={() => setShowRecordingStats(true)}
        onLocationInfo={() => setShowLocationInfo(true)}
        onDatabaseStatus={() => setShowDatabaseStatus(true)}
        onNewSurvey={handleNewSurvey}
        onViewMap={handleViewMap}
        onHighPoleSurvey={() => modalHandlers.setShowHighPoleSurvey(true)}
        onQuickSurvey={() => modalHandlers.setShowQuickSurvey(true)}
        onFileUpload={() => modalHandlers.setShowFileUpload(true)}
        onBookSurvey={() => modalHandlers.setShowBookSurvey(true)}
        onImport={() => modalHandlers.setShowImport(true)}
        onExport={() => modalHandlers.setShowExport(true)}
        onSharedMap={handleSharedMap}
        onArchives={() => navigate('/archives')}
        onRouteTimeline={() => navigate('/roadscope/timeline')}
        onPOIEditor={handlePOIEditor}
      />

      <SurveyList 
        surveys={surveys}
        loading={loading}
        error={error}
        activeSurvey={activeSurvey}
        isRecording={isRecording}
        onEdit={handleSurveyEdit} 
        onLoad={handleLoadSurvey}
      />

      {/* Edit Survey Modal */}
      {showEditSurvey && surveyToEdit && (
        <SurveyForm
          survey={surveyToEdit}
          isEdit={true}
          onClose={() => {
            setShowEditSurvey(false);
            setSurveyToEdit(null);
          }}
          onSave={handleSurveyUpdate}
          refetch={refetch}
        />
      )}

      {showNewSurveyForm && (
        <SurveyForm
          onClose={() => setShowNewSurveyForm(false)}
          onSave={async (data) => {
            await handleSurveyUpdate(data);
           // Force refetch after a short delay to ensure Firestore has time to update
           setTimeout(() => {
             refetch();
           }, 500);
          }}
          refetch={refetch}
        />
      )}

      {/* Modals */}
      {showShare && activeSurvey && (
        <ShareSurveyModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          survey={activeSurvey}
        />
      )}
      <Modals 
        states={modalStates}
        handlers={modalHandlers}
        activeSurvey={activeSurvey}
        teamMembers={teamMembers}
        onToast={setToast}
      />

      {/* Status Modals */}
      <RecordingStatsModal
        isOpen={showRecordingStats}
        onClose={() => setShowRecordingStats(false)}
      />
      <LocationInfoModal
        isOpen={showLocationInfo}
        onClose={() => setShowLocationInfo(false)}
      />
      <DatabaseStatusModal
        isOpen={showDatabaseStatus}
        onClose={() => setShowDatabaseStatus(false)}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default RoadScopePage;

export { RoadScopePage };